import type { ApiResponse } from "@/types/api";
import { SESSION_NAME } from '@/config/routes';
/**
 * API 클라이언트 모듈
 * 모든 API 요청의 기본 설정과 공통 로직을 관리합니다.
 */


// API 설정 가져오기
import { API_BASE_URL, API_TIMEOUT } from '@/config/api';

// API 요청 타입 정의
type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// API 요청 옵션 타입 정의
interface RequestOptions {
  headers?: Record<string, string>;
  body?: any;
  params?: Record<string, string>;
  formData?: FormData;
}

/**
 * API 요청 함수
 * @param method HTTP 메서드
 * @param endpoint API 엔드포인트 (기본 URL 이후 경로)
 * @param options 요청 옵션 (헤더, 바디, 파라미터 등)
 * @param token 인증 토큰 (선택적)
 * @returns Promise<ApiResponse<T>> API 응답
 */
export async function apiRequest<T>(
  method: RequestMethod,
  endpoint: string,
  options: RequestOptions = {},
  token?: string
): Promise<ApiResponse<T>> {
  // URL 파라미터 처리
  let url = `${API_BASE_URL}${endpoint}`;
  if (options.params) {
    const searchParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      searchParams.append(key, value);
    });
    url += `?${searchParams.toString()}`;
  }

  // 기본 헤더 설정
  const headers: Record<string, string> = {
    ...options.headers,
  };
  
  // FormData가 아닌 경우에만 Content-Type 설정
  if (!options.formData) {
    headers['Content-Type'] = 'application/json';
  }

  // 인증 토큰 처리
  // 1. 파라미터로 전달된 토큰이 있으면 사용
  // 2. 없으면 세션 스토리지에서 가져오기 시도
  let authToken = token;
  if (!authToken && typeof window !== 'undefined') {
    try {
      const authStorage = window.sessionStorage.getItem(SESSION_NAME);
      if (authStorage) {
        const authData = JSON.parse(authStorage);
        authToken = authData?.state?.token;
      }
    } catch (error) {
      console.error('[API] 세션 스토리지에서 토큰 가져오기 실패:', error);
    }
  }
  
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  // Fetch API 요청 옵션 구성
  const fetchOptions: RequestInit = {
    method,
    headers,
    credentials: 'same-origin', // CORS 오류 해결을 위해 'include'에서 'same-origin'으로 변경
  };

  // 요청 바디 처리
  if (['POST', 'PUT', 'PATCH'].includes(method)) {
    if (options.formData) {
      fetchOptions.body = options.formData;
    } else if (options.body) {
      fetchOptions.body = JSON.stringify(options.body);
    }
  }

  try {
    const response = await fetch(url, fetchOptions);

    // JSON 응답 파싱
    const data = await response.json() as ApiResponse<T>;
    return data;
  } catch (error) {
    console.error('[API] 요청 오류:', error);
    throw error;
  }
}

/**
 * 파일 다운로드 전용 API 요청 함수
 * @param method HTTP 메서드
 * @param endpoint API 엔드포인트
 * @param options 요청 옵션
 * @param token 인증 토큰
 * @returns Promise<Blob> 파일 데이터
 */
export async function apiDownloadRequest(
  method: RequestMethod,
  endpoint: string,
  options: RequestOptions = {},
  token?: string
): Promise<Blob> {
  // URL 파라미터 처리
  let url = `${API_BASE_URL}${endpoint}`;
  if (options.params) {
    const searchParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      searchParams.append(key, value);
    });
    url += `?${searchParams.toString()}`;
  }

  // 기본 헤더 설정
  const headers: Record<string, string> = {
    ...options.headers,
  };

  // 인증 토큰 처리
  let authToken = token;
  if (!authToken && typeof window !== 'undefined') {
    try {
      const authStorage = window.sessionStorage.getItem(SESSION_NAME);
      if (authStorage) {
        const authData = JSON.parse(authStorage);
        authToken = authData?.state?.token;
      }
    } catch (error) {
      console.error('[API] 세션 스토리지에서 토큰 가져오기 실패:', error);
    }
  }
  
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  // Fetch API 요청 옵션 구성
  const fetchOptions: RequestInit = {
    method,
    headers,
    credentials: 'same-origin',
  };

  try {
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Blob 응답 반환
    return await response.blob();
  } catch (error) {
    console.error('[API] 파일 다운로드 요청 오류:', error);
    throw error;
  }
}

/**
 * API 요청 헬퍼 함수들
 */
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions, token?: string) =>
    apiRequest<T>('GET', endpoint, options, token),
    
  post: <T>(endpoint: string, options?: RequestOptions, token?: string) =>
    apiRequest<T>('POST', endpoint, options, token),
    
  put: <T>(endpoint: string, options?: RequestOptions, token?: string) =>
    apiRequest<T>('PUT', endpoint, options, token),
    
  delete: <T>(endpoint: string, options?: RequestOptions, token?: string) =>
    apiRequest<T>('DELETE', endpoint, options, token),
    
  patch: <T>(endpoint: string, options?: RequestOptions, token?: string) =>
    apiRequest<T>('PATCH', endpoint, options, token),
};
